/* This module should be the one we keep using for google analytics events
 * It uses the logic created by Carlos to handle the events in a performant way.
 *
 * How to use it: dev should add four data attributes to the element it wants to track:
 * data-event='true'
 * data-ga-event-category='my event category'
 * data-ga-event-action='my event action'
 * data-ga-event-label='my event label'
 * The data-ga-event-label is optional, if not included, it will use the text of the element.
 *
 * Data Values for impression tracker:
 * data-impression="true"
 * data-ga-impression="true"
 * data-ga-impression-event-category='event category'
 * data-ga-impression-event-action='event action'
 * data-ga-impression-event-label= 'event label'
 *
 * The event tracker listens for the click function,
 * if for some reason there's an event.preventDefault that prevents tracking your event,
 * add a data-event-type="mousedown" for desktop or data-event-type="touchend" for mobile.
 *
 * If you need to track a event programatically instead of when clicking an element,
 * use the onDemandTracker method instead. Example:
 *
 *  if (global.NewHomeSource.Analytics.onDemandTracker) {
 *    const myEvent = {
 *      event: 'GAEvent',
 *      eventCategory: 'my event category',
 *      eventAction: 'my event action',
 *      eventLabel: 'my event label',
 *  };
 *
 *  global.NewHomeSource.Analytics.onDemandTracker.push(myEvent);
*/

(function googleAnalyticsTracker(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Analytics = global.NewHomeSource.Analytics || {};

  /**
   * Constant flag to avoid log new user event in wordpress pages.
   */
  const isNhs = !!global.NewHomeSource.Modules.cookieHandler;

  const configurations = {
    customDimensionsAttribute: 'gaDimension'
  };

  /*
  * Dependencies as
  * if (!global.NewHomeSource.MyDependency) {
  *   return;
  * }
  */

  const previousModule = global.NewHomeSource.Analytics.GoogleAnalyticsTracker;

  /**
   * @constructs NewHomeSource.Analytics.GoogleAnalyticsTracker
   */
  function GoogleAnalyticsTracker(globalValues) {
    configurations.globalValues = globalValues;
  }

  function pushToDataLayer(data) {
    const dataLayer = window.dataLayer;

    if (dataLayer) {
      dataLayer.push(data);
    }
  }

  /**
   * @private
   * getAttributes: get the attributes that starts with data-ga-dimension
   *
   * @param {DOMStringMap} dataset properties of the object.
   * @param {*} prefix to evaluate the properties we want to retrieve.
   * @param {*} attribute being evaluated.
   * @returns object with the property.
   */
  function getAttributes(dataset, prefix, attribute) {
    if (!attribute.startsWith(prefix)) {
      return;
    }

    const cleanProperty = attribute.replace(prefix, '');
    const name = cleanProperty.charAt(0).toLowerCase() + cleanProperty.slice(1);

    if (!name) {
      return;
    }

    this[name] = dataset[attribute];
  }

  /**
   * @private
   * getCustomDimension: gets a custom dimensions object based on data-ga-dimension attributes
   *
   * @param {DOMStringMap} dataset properties of the object.
   */
  function getCustomDimension(dataset) {
    const customDimensions = {};
    Object.keys(dataset)
      .forEach(getAttributes.bind(customDimensions, dataset, configurations.customDimensionsAttribute));

    return customDimensions;
  }

  /**
   * Pushes the google analytics event to the dataLayer.
   * @param {*} eventCategory Google analytics category name for the event.
   * @param {*} eventAction Google analytics action name for the event.
   * @param {*} eventLabel Google analytics label name for the event.
   * @param {*} eventNoInteraction Google analytics label name for the event.
   */
  function googlePush(eventCategory, eventAction, eventLabel, eventNoInteraction) {
    const dataLayer = window.dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'GAEvent',
        eventCategory,
        eventAction,
        eventLabel,
        eventValue: undefined,
        nonInteraction: eventNoInteraction || 0
      });
    }
  }

  /**
   * gaTrack: log google analytics event with google analytics object.
   *
   * @param {*} eventCategory Google analytics category name for the event.
   * @param {*} eventAction Google analytics action name for the event.
   * @param {*} eventLabel Google analytics label name for the event.
   */
  function gaTrack(eventCategory, eventAction, eventLabel) {
    pushToDataLayer({
      event: 'GAEvent',
      eventCategory,
      eventAction,
      eventLabel,
      eventValue: undefined,
      nonInteraction: 1
    });
  }

  function logNewUser() {
    const newUserGa = global.NewHomeSource.Modules.cookieHandler.readCookie('_ga');
    if (global.dataLayer && !newUserGa) {
      global.dataLayer.push({
        event: 'newUser',
        ga_newUser: 'true'
      });
    }
  }

  function setFullUrlsAsEventLabel(data) {
    const dataset = data;
    if (dataset.gaLabelFullUrl === 'true') {
      dataset.gaEventLabel = global.location.href;
    }
    return dataset;
  }

  /**
   * cleanCustomDimensions: pushes to the data layer the default values for all custom dimensions
   * recently pushed in an event. We need this for scenarios like Search Results Pages where
   * we don't want to keep in the data layer specific values like listing Ids recently clicked since
   * not all of the upcoming events are going to be bound to this particular Id.
   *
   * @param {*} dimensions is the custom dimensions for an event recently pushed.
   */
  function cleanCustomDimensions(dimensions) {
    const cleanDimensions = Object.assign({}, dimensions);
    Object.keys(cleanDimensions).forEach((dimension) => { cleanDimensions[dimension] = null; });

    pushToDataLayer(cleanDimensions);
  }

  GoogleAnalyticsTracker.onDemandTracker = function () {
    const dataLayer = window.dataLayer;
    const self = Object.assign({}, this);

    if (dataLayer) {
      dataLayer.push(self);
    }

    return true;
  };

  GoogleAnalyticsTracker.eventTracker = function () {
    const dataset = setFullUrlsAsEventLabel(this.dataset);
    const eventCategory = dataset.gaEventCategory;
    const eventAction = dataset.gaEventAction;

    const customDimensions = getCustomDimension(dataset);
    pushToDataLayer(customDimensions);

    if (eventCategory && eventAction) {
      let eventLabel = dataset.gaEventLabel;
      const eventNoInteraction = +dataset.gaEventNoInteraction || 0;

      if (!eventLabel && dataset.gaEventLabelEmpty !== 'true') {
        eventLabel = this.textContent.trim() + ' Clicks';
      }

      googlePush(eventCategory, eventAction, eventLabel, eventNoInteraction);

      if (this.dataset.cleanCustomDimensions) {
        cleanCustomDimensions(customDimensions);
      }
    }

    if (this.dataset.ctaDetail) {
      this.dataset.ctaDetail.split(',').forEach((data) => pushToDataLayer({ ctaDetail: data }));
    }

    return true;
  };

  /**
  * @public
  * impressionTrackerSelector: returns the expression that can be used to query the DOM for impression events.
  */
  GoogleAnalyticsTracker.impressionTrackerSelector = function () {
    return 'data-ga-impression';
  };

  /**
   * @public
   * impressionTracker: send data related to display events.
   *
   * @param {object} status of the scheduled idle task.
   */
  GoogleAnalyticsTracker.impressionTracker = function () {
    const length = this.length;
    for (let index = 0; index < length; index += 1) {
      const data = this[index].dataset;
      const eventCategory = data.gaImpressionEventCategory;
      const eventAction = data.gaImpressionEventAction;
      const eventLabel = data.gaImpressionEventLabel;
      const isImpressionGa = data.gaImpression;

      if (eventCategory && isImpressionGa && eventAction) {
        gaTrack(eventCategory, eventAction, eventLabel || null);
      }
    }
    return true;
  };

  /**
   * @public
   * identityTracker: send data related when user send the form.
   *
   */
  GoogleAnalyticsTracker.identityTracker = function identityTracker() {
    const dataset = this.form.querySelector('[data-ga-identity-category]').dataset;
    googlePush(dataset.gaIdentityCategory, dataset.gaIdentityAction, dataset.gaIdentityLabel || '');
    return true;
  };

  GoogleAnalyticsTracker.eventTrackerValidator = function (dataSet) {
    if ((dataSet.gaEventCategory && dataSet.gaEventAction) || dataSet.gaEventTracker) {
      return true;
    }

    return false;
  };

  GoogleAnalyticsTracker.onDemandTrackerValidator = function (data) {
    return data && data.eventType === 'GAEvent';
  };

  /**
   * @public
   * identityTrackerValidator: validates if the dataset passed as parameter represents a google analytics identity
   * being tracked.
   */
  GoogleAnalyticsTracker.identityTrackerValidator = function identityTrackerValidator(dataset) {
    return !!dataset.gaIdentityCategory && !!dataset.gaIdentityAction;
  };

  /**
   * @public
   * buildTagWithEvent: build tag to have the attributes to log analytics event.
   *
   * @param {Element} element to add the attributes.
   * @param {String} category Google analytics category name for the event.
   * @param {String} action Google analytics action name for the event.
   * @param {String} label Google analytics label name for the event.
   */
  GoogleAnalyticsTracker.buildTagWithEvent = function (element, category, action, label) {
    if (element) {
      element.setAttribute('data-event', 'true');
      element.setAttribute('data-ga-event-category', category);
      element.setAttribute('data-ga-event-action', action);
      element.setAttribute('data-ga-event-label', label);
    }
  };

  /**
   * @public
   * noConflict: Returns control of NewHomeSource.SampleModule to the previous script.
   */
  GoogleAnalyticsTracker.noConflict = function noConflict() {
    global.NewHomeSource.Analytics.GoogleAnalyticsTracker = previousModule;
    return GoogleAnalyticsTracker;
  };

  global.NewHomeSource.Analytics.GoogleAnalyticsTracker = GoogleAnalyticsTracker;
  if (isNhs) {
    logNewUser();
  }

  return GoogleAnalyticsTracker;
}(window));
